import { useMutation } from "@tanstack/react-query";
import { Loader2, Send } from "lucide-react";
import React from "react";

import { Button, Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context/i18n";

import * as campaignsApi from "../../campaign-details.api";
import { TCampaignContact } from "../../campaign-details.types";

/** No painel o fluxo não usa o template Meta; exibe saudação + corpo (o corpo vem do enriquecimento, sem saudação). */
function buildFlowTestFirstAssistantMessage(
  contact: TCampaignContact,
  translate: (key: string) => string
): string {
  const body = (contact.initial_message ?? "").trim();
  if (!body) {
    return "";
  }
  const firstName =
    contact.first_name?.trim() ||
    contact.name?.split(/\s+/)?.[0]?.trim() ||
    "";
  const salutation = firstName
    ? translate("campaigns.details.chat.openingNamed").replace(/\{\{name\}\}/g, firstName)
    : translate("campaigns.details.chat.openingGeneric");
  return `${salutation}\n\n${body}`;
}

/** Remove markdown simples (**negrito**, * lista) para exibir texto puro no chat. */
function stripMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^[\s]*[-*]\s+/gm, "• ")
    .trim();
}

function linkifyText(text: string): React.ReactNode {
  const cleaned = stripMarkdown(text);
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = cleaned.split(urlRegex);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary underline break-all"
      >
        {part}
      </a>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
}

export interface ICampaignFlowChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: TCampaignContact;
  campaignId: string;
  lpUrl?: string;
  /** Id estável por abertura do teste (ex.: chave do painel); evita contexto antigo no agente. */
  flowSessionId: string;
}

export const CampaignFlowChat: React.FC<ICampaignFlowChatProps> = ({
  open,
  onOpenChange,
  contact,
  campaignId,
  lpUrl,
  flowSessionId,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const [inputValue, setInputValue] = React.useState("");
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const messagesStartRef = React.useRef<HTMLDivElement>(null);

  const initialMessage = buildFlowTestFirstAssistantMessage(contact, t);
  const [messages, setMessages] = React.useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >(() =>
    initialMessage
      ? [{ role: "assistant" as const, content: initialMessage }]
      : []
  );

  const messagesRef = React.useRef(messages);
  React.useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (justOpened) {
      const opening = buildFlowTestFirstAssistantMessage(contact, t);
      if (opening) {
        setMessages([{ role: "assistant", content: opening }]);
      } else {
        setMessages([]);
      }
      setInputValue("");
    }
  }, [open, contact, t]);

  React.useEffect(() => {
    const scrollToShowFirstMessage = () => {
      if (messages.length <= 1) {
        messagesStartRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    };
    scrollToShowFirstMessage();
    const t = requestAnimationFrame(scrollToShowFirstMessage);
    return () => cancelAnimationFrame(t);
  }, [messages]);

  const chatMutation = useMutation({
    mutationFn: (userContent: string) => {
      const nextMessages = [
        ...messagesRef.current,
        { role: "user" as const, content: userContent },
      ];
      const apiMessages: campaignsApi.TCampaignContactChatMessage[] =
        nextMessages.map((m) => ({ role: m.role, content: m.content }));
      return campaignsApi.postCampaignContactChat(campaignId, contact.id, {
        messages: apiMessages,
        lp_url: lpUrl,
        session_id: flowSessionId,
      });
    },
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.content },
      ]);
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed || chatMutation.isPending) return;
      setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
      setInputValue("");
      chatMutation.mutate(trimmed);
    },
    [inputValue, chatMutation]
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col sm:max-w-xl sm:min-w-[28rem]"
      >
        <SheetHeader>
          <SheetTitle>{t("campaigns.details.chat.title")}</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col min-h-0 mt-4">
          <div className="flex-1 overflow-y-auto space-y-5 px-4 pb-4">
            <div ref={messagesStartRef} />
            {messages.map((msg, i) => (
              <div
                key={i}
                className={
                  msg.role === "user"
                    ? "flex justify-end"
                    : "flex justify-start"
                }
              >
                <div
                  className={
                    msg.role === "user"
                      ? "rounded-xl bg-primary text-primary-foreground px-4 py-3 max-w-[92%] overflow-visible"
                      : "rounded-xl bg-muted px-4 py-3 max-w-[92%] overflow-visible"
                  }
                >
                  <p className="text-sm leading-relaxed whitespace-pre-wrap break-words overflow-visible min-w-0">
                    {msg.role === "assistant"
                      ? linkifyText(msg.content)
                      : msg.content}
                  </p>
                </div>
              </div>
            ))}
            {chatMutation.isPending && (
              <div className="flex justify-start">
                <div className="rounded-xl bg-muted px-4 py-3 flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {t("campaigns.details.chat.typing")}
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form onSubmit={handleSubmit} className="flex gap-2 p-4 pt-3 border-t">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t("campaigns.details.chat.placeholder")}
              disabled={chatMutation.isPending}
              className="flex-1"
            />
            <Button
              type="submit"
              size="icon"
              disabled={!inputValue.trim() || chatMutation.isPending}
              aria-label={t("campaigns.details.chat.send")}
            >
              {chatMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
};
